import { Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { ComputerService } from '../computer.service';
import { LabelProfileMode, PrinterTarget, User } from 'src/typings';
import { ThermalPrintTargetService } from '../thermal-print-target.service';

type LabelTarget = PrinterTarget & { targetId: string; label: string; status: 'online' | 'offline' };

@Component({
  selector: 'app-label-print',
  templateUrl: './label-print.component.html',
  styleUrls: ['./label-print.component.scss']
})
export class LabelPrintComponent implements OnInit, OnDestroy {
  @Input() barcode = '';
  @Input() productId?: number;
  @Input() productTitle = '';
  @Input() warehouseId?: number;
  @Input() labelProfileMode: LabelProfileMode = 'printer_target';

  labelFrom: UntypedFormGroup = this.fb.group({
    agentTargetId: new UntypedFormControl('', Validators.required),
    quanPrint: new UntypedFormControl(1, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]),
    quanInPackage: new UntypedFormControl(1)
  });
  computers: LabelTarget[] = [];
  user: User | undefined;
  loadingTargets = false;
  printing = false;
  errorMessage = '';
  private readonly userSubscription: Subscription;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly computerService: ComputerService,
    private readonly notificationService: NotificationService,
    private readonly store: Store<AppState>,
    private readonly thermalPrintTargetService: ThermalPrintTargetService,
    @Optional() private readonly dialogRef: MatDialogRef<LabelPrintComponent> | null
  ) {
    this.userSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
    });
  }

  ngOnInit(): void {
    void this.loadTargets();
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  async loadTargets(): Promise<void> {
    if (this.loadingTargets || this.printing) return;
    this.loadingTargets = true;
    this.errorMessage = '';
    const agentControl = this.labelFrom.controls.agentTargetId;
    const previous = agentControl.value;
    agentControl.disable();
    try {
      const profile = this.user?.defaultLabelProfile;
      this.computers = (await this.thermalPrintTargetService.getSelectableTargets(
        'label', undefined, undefined,
        this.labelProfileMode === 'printer_target' ? null : profile?.profileId ?? profile?.id
      )).filter((target) => target.status === 'online');
      const selected = this.computers.find((target) => target.targetId === previous)
        || this.computers.find((target) => target.agentName === profile?.agentName)
        || this.computers[0];
      agentControl.setValue(selected?.targetId || '');
      if (!selected) this.errorMessage = 'Нет доступных онлайн-агентов печати. Подключите агент и обновите список.';
    } catch (error) {
      this.computers = [];
      agentControl.setValue('');
      this.showError(error, 'Не удалось загрузить агенты печати');
    } finally {
      agentControl.enable();
      this.loadingTargets = false;
    }
  }

  async print(): Promise<void> {
    if (this.printing || this.loadingTargets) return;
    this.errorMessage = '';
    const copies = Number(this.labelFrom.value.quanPrint);
    if (!Number.isSafeInteger(copies) || copies < 1) {
      this.labelFrom.controls.quanPrint.markAsTouched();
      this.errorMessage = 'Укажите целое количество штрих-кодов, не меньше 1.';
      return;
    }
    const computer = this.computers.find((target) =>
      target.targetId === this.labelFrom.value.agentTargetId && target.status === 'online');
    if (!computer) {
      this.errorMessage = 'Выберите онлайн-агент печати.';
      return;
    }
    if (!this.user || (!this.barcode && !this.productId)) {
      this.errorMessage = 'Не удалось определить пользователя или товар для печати.';
      return;
    }
    const profile = this.user.defaultLabelProfile;
    if (this.labelProfileMode !== 'printer_target' && !profile) {
      this.errorMessage = 'Выберите профиль этикеток в настройках.';
      return;
    }
    this.printing = true;
    this.labelFrom.controls.agentTargetId.disable();
    if (this.dialogRef) this.dialogRef.disableClose = true;
    try {
      await this.computerService.printProductLabel({
        productId: this.productId ?? (String(this.barcode || '').match(/^\d+$/) ? Number(this.barcode) : null),
        quanInPackage: Number(this.labelFrom.value.quanInPackage || 1),
        quanPrint: copies,
        barcode: String(this.barcode || '').trim() || undefined,
        warehouseId: this.warehouseId ?? this.user.defaultWarehouse?.id ?? null,
        profileId: this.labelProfileMode === 'printer_target' ? null : profile?.profileId ?? profile?.id ?? null,
        profileCode: this.labelProfileMode === 'printer_target' ? null : profile?.profileCode ?? profile?.code ?? null,
        labelProfileMode: this.labelProfileMode,
        agentName: computer.agentName ?? null,
        agentDeviceId: computer.deviceId ?? null
      });
      this.notificationService.notice('Штрих-коды отправлены на печать');
      this.dialogRef?.close(true);
    } catch (error) {
      this.showError(error, 'Не удалось отправить штрих-коды на печать');
    } finally {
      this.printing = false;
      this.labelFrom.controls.agentTargetId.enable();
      if (this.dialogRef) this.dialogRef.disableClose = false;
    }
  }

  close(): void {
    if (!this.printing) this.dialogRef?.close(false);
  }

  private showError(error: any, fallback: string): void {
    this.errorMessage = extractApiErrorMessage(error?.error || error, fallback);
    this.notificationService.showError(this.errorMessage);
  }
}
